import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { PublicacionesPlataformaService } from '../../services/publicaciones-plataforma.service';
import { MantenimientoPlataformaPublico } from '../../services/workspace/DTO/publicacion-plataforma.dto';

@Component({
  selector: 'app-mantenimiento-plataforma',
  templateUrl: './mantenimiento-plataforma.component.html',
  styleUrls: ['./mantenimiento-plataforma.component.scss'],
})
export class MantenimientoPlataformaComponent implements OnInit, OnDestroy {
  mantenimientoActivo: MantenimientoPlataformaPublico | null = null;
  tiempoRestante = '--:--:--';
  porcentajeAvance = 0;
  private subscriptions = new Subscription();
  private contador: any;

  constructor(
    private publicacionesService: PublicacionesPlataformaService,
    private router: Router
  ) {}

  /**
   * Conserva disponible la autenticacion para que el backend pueda comprobar
   * el permiso interno; las rutas funcionales siguen cubiertas por el bloqueo.
   */
  get mostrarBloqueo(): boolean {
    return !!this.mantenimientoActivo && !this.router.url.startsWith('/login');
  }

  /**
   * Inicia la vigilancia global y conecta la ventana activa con la capa
   * bloqueante; los avisos previos viven dentro de cada layout visible.
   */
  ngOnInit(): void {
    this.subscriptions.add(
      this.publicacionesService.activo$.subscribe(mantenimiento => {
        this.mantenimientoActivo = mantenimiento;
        this.iniciarContador();
      })
    );
    this.publicacionesService.iniciar();
  }

  /**
   * Libera las observaciones del componente raiz aunque el servicio singleton
   * mantenga el sondeo que protege el resto de la sesion.
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    clearInterval(this.contador);
  }

  /**
   * Reinicia el contador con cada ventana publicada y lo mantiene sincronizado
   * cada segundo con el desfase conocido de la hora central.
   */
  private iniciarContador(): void {
    clearInterval(this.contador);
    this.actualizarContador();
    if (this.mantenimientoActivo) {
      this.contador = setInterval(() => this.actualizarContador(), 1000);
    }
  }

  /**
   * Calcula tiempo restante y avance dentro de los limites autoritativos sin
   * alterar el momento en que el servicio libera el bloqueo.
   */
  private actualizarContador(): void {
    if (!this.mantenimientoActivo) {
      this.tiempoRestante = '--:--:--';
      this.porcentajeAvance = 0;
      return;
    }
    const inicio = new Date(this.mantenimientoActivo.fechaInicio).getTime();
    const fin = new Date(this.mantenimientoActivo.fechaFin).getTime();
    const ahora = this.publicacionesService.obtenerAhoraServidor();
    const restantes = Math.max(0, Math.floor((fin - ahora) / 1000));
    const horas = Math.floor(restantes / 3600);
    const minutos = Math.floor((restantes % 3600) / 60);
    const segundos = restantes % 60;
    this.tiempoRestante = [horas, minutos, segundos]
      .map(valor => String(valor).padStart(2, '0'))
      .join(':');
    const duracion = Math.max(1, fin - inicio);
    this.porcentajeAvance = Math.min(
      100,
      Math.max(0, ((ahora - inicio) / duracion) * 100)
    );
  }

  /**
   * Renderiza la fecha central en hora ecuatoriana aunque el navegador se abra
   * desde una zona distinta.
   */
  formatearHora(fecha: string): string {
    return new Intl.DateTimeFormat('es-EC', {
      timeZone: 'America/Guayaquil',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(fecha));
  }

  /**
   * Presenta fecha y hora completas en Ecuador para las filas operativas de la
   * ventana, incluyendo explicitamente la zona usada por el backend.
   */
  formatearFecha(fecha: string): string {
    return new Intl.DateTimeFormat('es-EC', {
      timeZone: 'America/Guayaquil',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(fecha));
  }

  /**
   * Convierte los minutos persistidos en una lectura breve para la ficha de la
   * ventana sin perder el valor exacto definido por administracion.
   */
  get duracionTexto(): string {
    if (!this.mantenimientoActivo) return '';
    const total = Number(this.mantenimientoActivo.duracionEstimadaMinutos);
    const horas = Math.floor(total / 60);
    const minutos = total % 60;
    if (!horas) return `${minutos} minutos`;
    if (!minutos) return `${horas} ${horas === 1 ? 'hora' : 'horas'}`;
    return `${horas} ${horas === 1 ? 'hora' : 'horas'} ${minutos} minutos`;
  }

  /**
   * Construye una referencia visual corta desde la UUID publica sin introducir
   * otro identificador persistente para el mismo mantenimiento.
   */
  get referencia(): string {
    if (!this.mantenimientoActivo) return '';
    return `MNT-${this.mantenimientoActivo.uuid.slice(0, 8).toUpperCase()}`;
  }

  /**
   * Solicita el estado central de inmediato para confirmar una finalizacion o
   * cancelacion sin recargar ni abandonar la pantalla actual.
   */
  reintentar(): void {
    this.publicacionesService.refrescar();
  }
}
